import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { AlertTriangle, Info } from 'lucide-react';

interface LCARSConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'info';
}

const LCARSConfirmationModal: React.FC<LCARSConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = 'PROCEED',
  cancelLabel = 'CANCEL',
  variant = 'danger'
}) => {
  const [isConfirming, setIsConfirming] = React.useState(false);
  const accentColor = variant === 'danger' ? 'bg-lcars-red' : 'bg-lcars-blue';
  const borderColor = variant === 'danger' ? 'border-lcars-red' : 'border-lcars-blue';
  const textColor = variant === 'danger' ? 'text-lcars-red' : 'text-lcars-blue';

  const handleConfirm = async () => {
    setIsConfirming(true);
    try {
      await onConfirm();
    } catch (err) {
      console.error("Confirmation action failed:", err);
    } finally {
      setIsConfirming(false);
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && !isConfirming && onClose()}>
      <DialogContent className="bg-[var(--background)] border-2 border-lcars-gold max-w-md p-0 overflow-hidden">
        {/* LCARS Header Bar */}
        <div className={`h-8 ${accentColor} flex items-center px-4 justify-between`}>
          <div className="flex gap-2">
            <div className="w-8 h-2 bg-[var(--background)]/20 rounded-full"></div>
            <div className="w-16 h-2 bg-[var(--background)]/20 rounded-full"></div>
          </div>
          <span className="text-black font-bold text-[0.6rem] tracking-widest uppercase">
            {isConfirming ? 'PROCESSING...' : 'SECURITY CLEARANCE REQUIRED'}
          </span>
        </div>

        <div className="p-6 space-y-4">
          <div className="flex items-start gap-4">
            <div className={`p-3 rounded-lg ${variant === 'danger' ? 'bg-lcars-red/10' : 'bg-lcars-blue/10'}`}>
              {variant === 'danger' ? (
                <AlertTriangle className={`w-6 h-6 ${textColor} ${isConfirming ? 'animate-pulse' : ''}`} />
              ) : (
                <Info className={`w-6 h-6 ${textColor} ${isConfirming ? 'animate-pulse' : ''}`} />
              )}
            </div>
            <div>
              <DialogTitle className="text-xl font-bold text-[var(--foreground)] uppercase tracking-tight mb-2">
                {title}
              </DialogTitle>
              <DialogDescription className="text-[var(--foreground)]/70 text-sm leading-relaxed">
                {description}
              </DialogDescription>
            </div>
          </div>
        </div>

        <DialogFooter className="p-4 bg-white/5 flex flex-row gap-2 justify-end border-t border-white/10">
          <button
            onClick={onClose}
            disabled={isConfirming}
            className="lcars-pill bg-white/10 text-[var(--foreground)] px-6 py-2 text-xs font-bold hover:bg-white/20 transition-all uppercase disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            onClick={handleConfirm}
            disabled={isConfirming}
            className={`lcars-pill ${accentColor} text-black px-6 py-2 text-xs font-bold transition-all uppercase disabled:opacity-50 flex items-center gap-2`}
          >
            {isConfirming && <div className="w-3 h-3 border-2 border-black border-t-transparent rounded-full animate-spin" />}
            {confirmLabel}
          </button>
        </DialogFooter>
        
        {/* LCARS Bottom Accent */}
        <div className="h-4 flex gap-1 p-1">
          <div className={`flex-1 rounded-bl-xl ${accentColor}`}></div>
          <div className="w-4 bg-lcars-gold"></div>
          <div className="w-12 bg-lcars-tan"></div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LCARSConfirmationModal;
